import React, { useRef } from "react";
import { Button } from "@mui/material";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslation } from "react-i18next";
import { Base64Amiri } from "../../../../assets/dist/base64Amiri";

const formatNumber = (value) => {
  const parsed = Number(value || 0);
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getNetPayable = (row) => {
  const explicitValue = Number(row?.total_net_payable);
  if (!Number.isNaN(explicitValue) && explicitValue > 0) {
    return explicitValue;
  }
  return Number(row?.total_net || 0) + Number(row?.total_net_vat || 0);
};

const sanitizeFileName = (value) =>
  (value || "gss_report")
    .replace(/[^a-z0-9-_]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const getContractNumber = (row) => {
  const value = row?.contract_no ?? row?.contractNo ?? row?.ContractNo ?? "";
  const normalized = String(value || "").trim();
  return normalized || "N/A";
};

function PDFDownload({ payer, headerInfo }) {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const reports = payer?.reports || [];

  const totals = reports.reduce(
    (acc, report) => ({
      count: acc.count + Number(report?.count || 0),
      gross: acc.gross + Number(report?.total_gross || 0),
      discount: acc.discount + Number(report?.total_discount || 0),
      deductible: acc.deductible + Number(report?.total_patient_share || 0),
      netAmount: acc.netAmount + Number(report?.total_net || 0),
      vat: acc.vat + Number(report?.total_net_vat || 0),
      netPayable: acc.netPayable + getNetPayable(report),
    }),
    {
      count: 0,
      gross: 0,
      discount: 0,
      deductible: 0,
      netAmount: 0,
      vat: 0,
      netPayable: 0,
    },
  );

  const totalClaims = Number(payer?.total_claims || headerInfo?.totalClaims || totals.count || 0);
  const claimTypeText = (payer?.claim_type_counts || [])
    .map((item) => {
      const label = item?.claim_type || t("Unknown");
      const count = Number(item?.count || 0);
      return `${label} (${count})`;
    })
    .filter(Boolean)
    .join(", ");

  const downloadPDF = async () => {
    const input = tableRef.current;
    if (!input) {
      return;
    }

    const fontName = "Amiri";
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addFileToVFS(`${fontName}.ttf`, Base64Amiri);
    pdf.addFont(`${fontName}.ttf`, fontName, "normal");
    pdf.setFont(fontName);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    const headerLines = (value) =>
      String(value ?? "___")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    // ----- Title block -----
    pdf.setFont(fontName, "normal");
    pdf.setFontSize(15);
    pdf.setTextColor(43, 124, 188);
    pdf.text("General Summary Statement", margin, margin + 6);

    pdf.setFontSize(10);
    pdf.setTextColor(93, 104, 115);
    pdf.text(
      String(payer?.payer_name || headerInfo?.payerName || "___"),
      margin,
      margin + 12,
    );

    // ----- Header fields (bordered grid, label/value pairs) -----
    pdf.setFontSize(10);
    pdf.setTextColor(51, 51, 51);
    const leftHeader = [
      [`${t("CCHI NO.")}`, String(headerInfo?.cchiNo || "___")],
      [`${t("Provider Name")}`, String(headerInfo?.providerName || "___")],
      [`${t("Claim Type")}`, String(claimTypeText || headerInfo?.claimType || "___")],
    ];
    const rightHeader = [
      [`${t("Batch Month")}`, String(headerInfo?.batchMonth || "___")],
      [`${t("Batch ID")}`, String(headerInfo?.batchId || payer?.payer_id || "___")],
      [`${t("Batch Type")}`, String(headerInfo?.batchType || "Regular")],
      [`${t("Total Claims")}`, String(totalClaims)],
    ];

    const headerRows = [];
    const headerRowCount = Math.max(leftHeader.length, rightHeader.length);
    for (let i = 0; i < headerRowCount; i += 1) {
      const left = leftHeader[i] || ["", ""];
      const right = rightHeader[i] || ["", ""];
      headerRows.push([left[0], left[1], right[0], right[1]]);
    }

    autoTable(pdf, {
      startY: margin + 18,
      margin: { left: margin, right: margin },
      body: headerRows,
      theme: "grid",
      styles: {
        font: fontName,
        fontSize: 9.5,
        cellPadding: 2,
        textColor: [51, 51, 51],
        lineColor: [215, 215, 215],
        lineWidth: 0.15,
      },
      columnStyles: {
        0: { cellWidth: 34, halign: "left", fontStyle: "bold" },
        1: { cellWidth: (contentWidth - 68) / 2, halign: "left" },
        2: { cellWidth: 34, halign: "left", fontStyle: "bold" },
        3: { cellWidth: (contentWidth - 68) / 2, halign: "left" },
      },
      didDrawPage: () => {},
    });

    const headerBottom = pdf.lastAutoTable.finalY + 5;

    // ----- Data table -----
    const bodyRows = reports.map((report, index) => [
      index + 1,
      headerLines(report?.policy_holder || "N/A"),
      getContractNumber(report),
      report?.count || 0,
      formatNumber(report?.total_gross),
      formatNumber(report?.total_discount),
      formatNumber(report?.total_patient_share),
      formatNumber(report?.total_net),
      formatNumber(report?.total_net_vat),
      formatNumber(getNetPayable(report)),
    ]);

    bodyRows.push([
      { content: t("Total"), colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
      totals.count,
      formatNumber(totals.gross),
      formatNumber(totals.discount),
      formatNumber(totals.deductible),
      formatNumber(totals.netAmount),
      formatNumber(totals.vat),
      formatNumber(totals.netPayable),
    ]);

    autoTable(pdf, {
      startY: headerBottom,
      margin: { left: margin, right: margin },
      head: [
        [
          { content: "", colSpan: 4, styles: { halign: "center" } },
          { content: t("Billed"), colSpan: 6, styles: { halign: "center" } },
        ],
        [
          t("No"),
          t("Group Name"),
          t("Contract No."),
          t("Count"),
          t("Gross"),
          t("Discount"),
          t("Deductible"),
          t("Net Amount"),
          t("VAT"),
          t("Net Payable"),
        ],
      ],
      body: bodyRows,
      styles: {
        font: fontName,
        fontSize: 9,
        cellPadding: 2,
        textColor: [51, 51, 51],
        lineColor: [216, 216, 216],
        lineWidth: 0.15,
      },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [51, 51, 51],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 12, halign: "center" },
        1: { cellWidth: 52, halign: "left" },
        2: { cellWidth: 30, halign: "left" },
        3: { cellWidth: 16, halign: "center" },
        4: { cellWidth: 29, halign: "right" },
        5: { cellWidth: 29, halign: "right" },
        6: { cellWidth: 29, halign: "right" },
        7: { cellWidth: 29, halign: "right" },
        8: { cellWidth: 25, halign: "right" },
        9: { cellWidth: 29, halign: "right" },
      },
      didParseCell: (data) => {
        // Group header row: left section (No..Count) keeps its borders;
        // Billed span is gray with no borders so only the left edge separates it
        if (data.section === "head" && data.row.index === 0) {
          if (data.cell.colSpan === 6) {
            data.cell.styles.lineWidth = 0;
            data.cell.styles.fillColor = [229, 229, 229];
          } else {
            data.cell.styles.fillColor = [255, 255, 255];
          }
        }
      },
      didDrawPage: () => {},
    });

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i += 1) {
      pdf.setPage(i);
      pdf.setFont(fontName, "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(
        `${t("Page")} ${i} ${t("of")} ${totalPages}`,
        pageWidth - margin,
        pageHeight - margin / 2,
        { align: "right" },
      );
    }

    pdf.save(`${sanitizeFileName(payer?.payer_name)}_gss_statement.pdf`);
  };

  if (!payer) {
    return null;
  }

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button variant="contained" color="primary" onClick={downloadPDF}>
          {t("Download PDF")}
        </Button>
      </div>

      <div
        ref={tableRef}
        className="mx-auto w-full max-w-[1220px] bg-white p-6 text-[#2f3742]"
        style={{ fontFamily: 'Arial, "Helvetica Neue", sans-serif' }}
      >
        <div className="border border-[#d7d7d7] bg-white px-5 py-4 shadow-sm">
          <div className="flex items-center gap-5 border-b border-[#d7d7d7] pb-4">
            <div className="text-[22px] font-normal text-[#2b7cbc]">
              {t("General Summary Statement")}
            </div>
            <div className="mt-1 text-[13px] text-[#5d6873]">
              {payer?.payer_name || headerInfo?.payerName || "___"}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-20 border-b border-[#d7d7d7] py-4 text-[13px] leading-7">
            <div>
              <div className="flex">
                <span className="w-[130px] font-semibold text-[#333]">{t("CCHI NO.")}</span>
                <span>{headerInfo?.cchiNo || "___"}</span>
              </div>
              <div className="flex">
                <span className="w-[130px] font-semibold text-[#333]">{t("Provider Name")}</span>
                <span>{headerInfo?.providerName || "___"}</span>
              </div>
              <div className="flex">
                <span className="w-[130px] font-semibold text-[#333]">{t("Claim Type")}</span>
                <span>{claimTypeText || headerInfo?.claimType || "___"}</span>
              </div>
            </div>

            <div>
              <div className="flex">
                <span className="w-[130px] font-semibold text-[#333]">{t("Batch Month")}</span>
                <span>{headerInfo?.batchMonth || "___"}</span>
              </div>
              <div className="flex">
                <span className="w-[130px] font-semibold text-[#333]">{t("Batch ID")}</span>
                <span>{headerInfo?.batchId || payer?.payer_id || "___"}</span>
              </div>
              <div className="flex">
                <span className="w-[130px] font-semibold text-[#333]">{t("Batch Type")}</span>
                <span>{headerInfo?.batchType || "Regular"}</span>
              </div>
              <div className="flex">
                <span className="w-[130px] font-semibold text-[#333]">{t("Total Claims")}</span>
                <span>{totalClaims}</span>
              </div>
            </div>
          </div>

          <div className="border-b border-[#d7d7d7] bg-[#f8fafc] px-3 py-2 text-[12px] text-[#334155]">

          </div>

          <table className="mt-0 w-full border-collapse text-[12px]">
            <thead>
              <tr>
                <th colSpan={4} className="border border-[#d8d8d8] bg-[#f0f0f0] px-2 py-2 text-left font-semibold text-[#333]"></th>
                <th colSpan={6} className="border-0 bg-[#e5e5e5] px-2 py-2 text-center font-semibold text-[#333]">
                  {t("Billed")}
                </th>
              </tr>
              <tr className="bg-[#f7f7f7] text-[#333]">
                <th className="border border-[#d8d8d8] px-2 py-2 text-left font-semibold">{t("No")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-left font-semibold">{t("Group Name")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-left font-semibold">{t("Contract No.")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-center font-semibold">{t("Count")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-right font-semibold">{t("Gross")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-right font-semibold">{t("Discount")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-right font-semibold">{t("Deductible")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-right font-semibold">{t("Net Amount")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-right font-semibold">{t("VAT")}</th>
                <th className="border border-[#d8d8d8] px-2 py-2 text-right font-semibold">{t("Net Payable")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report, index) => (
                <tr key={`${report?.policy_holder || "report"}-${index}`} className="align-top">
                  <td className="border border-[#e0e0e0] px-2 py-2">{index + 1}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 noto-sans-arabic leading-4">
                    {report?.policy_holder || "N/A"}
                  </td>
                  <td className="border border-[#e0e0e0] px-2 py-2">{getContractNumber(report)}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 text-center">{report?.count || 0}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 text-right">{formatNumber(report?.total_gross)}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 text-right">{formatNumber(report?.total_discount)}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 text-right">{formatNumber(report?.total_patient_share)}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 text-right">{formatNumber(report?.total_net)}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 text-right">{formatNumber(report?.total_net_vat)}</td>
                  <td className="border border-[#e0e0e0] px-2 py-2 text-right">{formatNumber(getNetPayable(report))}</td>
                </tr>
              ))}
              <tr className="bg-[#fafafa] font-semibold text-[#333]">
                <td colSpan={3} className="border border-[#d8d8d8] px-2 py-2 text-right">
                  {t("Total")}
                </td>
                <td className="border border-[#d8d8d8] px-2 py-2 text-center">{totals.count}</td>
                <td className="border border-[#d8d8d8] px-2 py-2 text-right">{formatNumber(totals.gross)}</td>
                <td className="border border-[#d8d8d8] px-2 py-2 text-right">{formatNumber(totals.discount)}</td>
                <td className="border border-[#d8d8d8] px-2 py-2 text-right">{formatNumber(totals.deductible)}</td>
                <td className="border border-[#d8d8d8] px-2 py-2 text-right">{formatNumber(totals.netAmount)}</td>
                <td className="border border-[#d8d8d8] px-2 py-2 text-right">{formatNumber(totals.vat)}</td>
                <td className="border border-[#d8d8d8] px-2 py-2 text-right">{formatNumber(totals.netPayable)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PDFDownload;
